import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Trash2, Plus, Copy, GripVertical } from "lucide-react"
import * as LucideIcons from "lucide-react"
import React from "react"
import type { SchemaSection, SchemaScoreField } from "@/api/types/schema"
import { ScoreFieldEditor } from "./ScoreFieldEditor"

interface SectionEditorProps {
  section: SchemaSection
  onSectionChange: (sectionUuid: string, updates: Partial<SchemaSection>) => void
  onDuplicate: (sectionId: string) => void
  onRemove: (sectionId: string) => void
  onAddScore: (sectionUuid: string) => void
  onRemoveScore: (sectionUuid: string, scoreId: string) => void
  onUpdateScore: (sectionUuid: string, scoreId: string, updates: Partial<SchemaScoreField>) => void
  isLoading?: boolean
}

// Get list of common Lucide icon names
const get_icon_names = (): string[] => {
  const common_icons = [
    'Building2', 'School', 'Users', 'Globe', 'Target', 'TrendingUp', 'TrendingDown',
    'BarChart', 'PieChart', 'Activity', 'Briefcase', 'Home', 'Settings', 'Info',
    'AlertCircle', 'CheckCircle', 'XCircle', 'Heart', 'Star', 'Flag', 'Shield',
    'Zap', 'Clock', 'Calendar', 'Map', 'Navigation', 'Compass', 'Camera'
  ]
  return common_icons.sort()
}

const get_icon_component = (icon_name?: string): React.ComponentType<any> | undefined => {
  if (!icon_name) return undefined
  return (LucideIcons as any)[icon_name]
}

export const SectionEditor = React.memo(({ 
  section, 
  onSectionChange,
  onDuplicate,
  onRemove,
  onAddScore,
  onRemoveScore,
  onUpdateScore,
  isLoading 
}: SectionEditorProps) => {
  const IconComponent = get_icon_component(section.icon)
  
  return (
    <div className="border rounded-lg p-4 space-y-4 bg-white">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <Badge variant="outline" className="text-xs">
            #{section.order}
          </Badge>
          {IconComponent && <IconComponent className="h-4 w-4" />}
          <span className="font-medium">{section.title || 'Untitled Section'}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDuplicate(section.id)}
            disabled={isLoading}
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(section.id)}
            disabled={isLoading}
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Section Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Section ID</Label>
          <Input
            value={section.id}
            onChange={(e) => onSectionChange(section.uuid, { id: e.target.value })}
            placeholder="section_id"
            className="font-mono text-sm"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            value={section.title}
            onChange={(e) => onSectionChange(section.uuid, { title: e.target.value })}
            placeholder="Section Title"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label>Display Order</Label>
          <Input
            type="number"
            value={section.order}
            onChange={(e) => {
              const num_val = e.target.value === '' ? 1 : parseInt(e.target.value, 10)
              if (!isNaN(num_val)) {
                onSectionChange(section.uuid, { order: num_val })
              }
            }}
            placeholder="1"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label>Icon</Label>
          <Select
            value={section.icon || '__none__'}
            onValueChange={(value) => onSectionChange(section.uuid, { icon: value === '__none__' ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select icon">
                {section.icon && (
                  <div className="flex items-center gap-2">
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    {section.icon}
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No Icon</SelectItem>
              {get_icon_names().map((icon_name) => {
                const Icon = get_icon_component(icon_name)
                return (
                  <SelectItem key={icon_name} value={icon_name}>
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="h-4 w-4" />}
                      {icon_name}
                    </div>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* Score Fields */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm">Score Fields</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddScore(section.uuid)}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Score
          </Button>
        </div>
        
        {section.scores.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded-lg">
            <p className="text-sm">No score fields configured</p>
            <p className="text-xs">Add score fields to define what the AI should analyze</p>
          </div>
        ) : (
          <div className="space-y-3">
            {section.scores.map((score) => (
              <ScoreFieldEditor
                key={score.uuid}
                score={score}
                sectionUuid={section.uuid}
                onUpdate={onUpdateScore}
                onRemove={onRemoveScore}
                isLoading={isLoading}
              />
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Rationale Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            id={`rationale-${section.uuid}`}
            checked={section.includeRationale}
            onCheckedChange={(checked) => onSectionChange(section.uuid, { includeRationale: checked })}
            disabled={isLoading}
          />
          <Label htmlFor={`rationale-${section.uuid}`} className="text-sm font-medium">
            Include Rationale
          </Label>
        </div>
        <div className="space-y-2">
          <Label>Rationale Title</Label>
          <Input
            value={section.details_title}
            onChange={(e) => onSectionChange(section.uuid, { details_title: e.target.value })}
            placeholder="Rationale"
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  )
})

SectionEditor.displayName = 'SectionEditor'
